// `extension-alert-candidates-slim.tpl.html:84` — the select-all checkbox, the selection count
// and the bulk actions, between the filters bar and the candidate rows.
import type { ReactElement } from 'react'
import { inlineBulkActions, overflowBulkActions, type BulkAction } from './bulkActions.ts'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { uibDropdown } from '../common/uibDropdown.ts'

export interface CandidateSelectionBarProps {
  actions: readonly BulkAction[]
  selectedCount?: number
  /** `data.isCheckboxDependent` — a bulk operation already owns the frame. */
  checkboxDependent?: boolean
  checkboxOperationLabel?: string
  selectMenuOpen?: boolean
  overflowOpen?: boolean
  onToggleSelectMenu?: () => void
  onToggleOverflow?: () => void
  onSelectAllInPage?: () => void
  onSelectAllInResults?: () => void
  onClearAll?: () => void
  onAction?: (action: BulkAction) => void
}

export function CandidateSelectionBar({
  actions,
  selectedCount = 0,
  checkboxDependent = false,
  checkboxOperationLabel,
  selectMenuOpen = false,
  overflowOpen = false,
  onToggleSelectMenu,
  onToggleOverflow,
  onSelectAllInPage,
  onSelectAllInResults,
  onClearAll,
  onAction,
}: CandidateSelectionBarProps): ReactElement {
  const inline = inlineBulkActions(actions)
  const overflow = overflowBulkActions(actions)
  const hasSelection = selectedCount > 0

  return (
    <div className={`candidate-selection-container slim-filters-and-selection-section${checkboxDependent ? ' message-on' : ''}`}>
      <div className="candidate-selection-checkbox">
        {/* The anonymous `<div uib-dropdown>` upstream writes. `.open` goes HERE, not on the
            toggle: `.open > .dropdown-menu` is a CHILD combinator and the menu is this div's
            child, the toggle's sibling. */}
        <div className={uibDropdown('', selectMenuOpen).trim()}>
          {/* ⚠️ `dropdown-toggle` IS NOT IN THE TEMPLATE — `uib-dropdown-toggle` adds it, and the
              stylesheet's only rule for this row is `.candidate-selection-checkbox
              .dropdown-toggle { display: flex }`. Without it the checkbox and the count stack
              instead of sitting side by side, the bar grows from 36px to 62px, and every row
              of the candidate list below it sits 26px too low — which reads as a list-spacing
              bug rather than as a missing class on a row two elements up. */}
          <div
            className="cursor-pointer hover-lighter dropdown-toggle"
            role="button"
            aria-label="Selection options"
            tabIndex={0}
            onClick={onToggleSelectMenu}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onToggleSelectMenu?.()
            }}
          >
            {/* `ng-disabled="true"` upstream — it REFLECTS whether anything is selected and
                is never clicked directly; the menu beside it is what selects. Disabled rather
                than merely read-only, because the stylesheet has a
                `:disabled + .ng-checkbox-indication` rule for exactly this one. */}
            <WooCheckbox className="candidate-slim-details-checkbox" resetStyle checked={hasSelection} disabled />
            <div className={`candidate-selection-count${hasSelection ? ' has-selection' : ''}`}>{selectedCount}</div>
          </div>
          {/* ⚠️ `dropdown-menu` IS THE CLASS `uib-dropdown-menu` ADDS, and leaving it off is
              why this looked broken. The template writes
              `<ul uib-dropdown-menu class="options-menu-item slim-view-show-options">` — the
              directive supplies `dropdown-menu`, the author's two classes style what is
              inside it. Without it the list is an ordinary inline `<ul>`: it lands in the
              flow of the selection bar, wraps across three lines and pushes the candidate
              rows down, instead of overlaying them from `position: absolute`. */}
          {selectMenuOpen ? (
            <ul className="dropdown-menu options-menu-item slim-view-show-options">
              <li>
                <a href="" onClick={(event) => { event.preventDefault(); onSelectAllInPage?.() }}>Select all in page</a>
              </li>
              <li>
                <a href="" onClick={(event) => { event.preventDefault(); onSelectAllInResults?.() }}>Select all results</a>
              </li>
              <li>
                <a href="" onClick={(event) => { event.preventDefault(); onClearAll?.() }}>Clear all</a>
              </li>
            </ul>
          ) : null}
        </div>
      </div>
      <div
        className="candidate-selection-bulk-actions"
        {...(checkboxDependent
          ? { title: `Please close the ${checkboxOperationLabel ?? ''} window to continue` }
          : {})}
      >
        {inline.map((action) => (
          <div key={action.name} className={`bulk-action slim-view-${action.name}-all`}>
            <i
              className={action.icon}
              title={action.title}
              role="button"
              aria-label={action.title}
              tabIndex={0}
              onClick={() => onAction?.(action)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                onAction?.(action)
              }}
            />
          </div>
        ))}
        {overflow.length > 0 ? (
          <div className="candidate-selection-bulk-actions-more">
            {/* `uib-dropdown` upstream. `_extension-alert-candidates-slim.scss:233` already
                carries an `&.open` rule for this element — the stylesheet was ported expecting
                the class that nothing was adding. */}
            {/* ⚠️ `top: 100%` NEEDS A POSITIONED HOST, and without one the menu walks up to the
                scrolling list and opens at ITS bottom — measured at 378×900 the menu landed at
                y=902, off the bottom of the frame entirely, which is what "missing or displayed
                out of view" was. `.dropdown-menu` is `position: absolute; top: 100%` and
                `.dropdown-menu-right` adds `right: 0`; bootstrap's own `.dropdown` class is
                what normally supplies the `position: relative`, and `uib-dropdown` is an
                ATTRIBUTE, so upstream gets it from this element's own chain. */}
            <div className={uibDropdown('bulk-actions-more-caret', overflowOpen)} style={{ position: 'relative' }}>
              <i
                className="far fa-ellipsis-v"
                title="More bulk actions"
                role="button"
                aria-label="More bulk actions"
                tabIndex={0}
                onClick={onToggleOverflow}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return
                  event.preventDefault()
                  onToggleOverflow?.()
                }}
              />
              {overflowOpen ? (
                <ul className="dropdown-menu dropdown-menu-right" role="menu">
                  {overflow.map((action) => (
                    <li key={action.name} className={`bulk-action-more slim-view-${action.name}-all`}>
                      <a
                        href=""
                        title={action.title}
                        onClick={(event) => {
                          event.preventDefault()
                          onAction?.(action)
                        }}
                      >
                        <i className={action.icon} />
                        {'   '}
                        {action.title}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
